/**
 * /forms — landing surface for eSource form work.
 *
 * Two-tab layout (client feedback 2026-07-27):
 *   1. "By Visit" (default) — visit-first workflow. Pick a study, click
 *      a visit, see every form in the study with a checkbox indicating
 *      whether it's assigned to that visit. Toggle to assign/unassign
 *      via mutable ctms_form_visit_bindings — no republish needed.
 *   2. "All Forms" (existing flat list) — the pre-existing form
 *      catalog view. Preserved so power users who work form-first
 *      still have their landing.
 *
 * "New Form" button lives in both tabs. In the By Visit tab, creating
 * a form here auto-assigns it to the currently-selected visit.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  X,
  CalendarDays,
  ListChecks,
  Check,
  Square,
  CheckSquare,
  Eye,
  Zap,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Lock,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ApiError,
  forms,
  formVisitBindings,
  schedule,
  studies,
  type ConditionExpr,
  type FormForVisitRow,
  type FormRow,
  type FormStatus,
  type VisitTemplateRow,
} from '@/api/client';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import { useHasRole } from '@/components/RoleGate';
import { fmtDateTime } from '@/lib/dates';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindedNotice } from '@/components/layout/BlindedNotice';
import { RuleBuilder } from '@/components/forms/RuleBuilder';
import { errorText } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

const STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const STATUS_BADGE: Record<FormStatus, string> = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const CONFIGURE_FORM_ROLES = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'regulatory_specialist',
] as const;

/** Attach published forms to a study's visits (server: study role on the study). */
const ATTACH_FORM_ROLES = [
  ...CONFIGURE_FORM_ROLES,
  'principal_investigator',
  'sub_investigator',
  'coordinator',
] as const;

type TabKey = 'by-visit' | 'all-forms';

// ---------------------------------------------------------------------------

export function FormsListPage() {
  const canConfigure = useHasRole(CONFIGURE_FORM_ROLES);
  const canAttach = useHasRole(ATTACH_FORM_ROLES);
  const [tab, setTab] = useState<TabKey>('by-visit');

  const studiesQ = useQuery({
    queryKey: ['studies', 'for-forms-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  const studyOptions: SingleSelectOption[] = useMemo(
    () =>
      (studiesQ.data?.rows ?? []).map((s) => ({
        value: s.study_id,
        label: studyLabel(s),
      })),
    [studiesQ.data],
  );
  // Site-wide "All Forms" needs to tell rows from different studies apart.
  const studyLabelById = useMemo(
    () => new Map(studyOptions.map((o) => [o.value, o.label])),
    [studyOptions],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="eSource forms"
        subtitle={
          tab === 'by-visit'
            ? 'Pick a study and click a visit to see the forms attached to it. Toggle a checkbox to assign or unassign.'
            : 'Study-scoped catalog of every eSource form. Drafts are visible to designers only.'
        }
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-surface-border">
        <TabButton
          active={tab === 'by-visit'}
          onClick={() => setTab('by-visit')}
          icon={<CalendarDays className="h-3.5 w-3.5" />}
          label="By Visit"
        />
        <TabButton
          active={tab === 'all-forms'}
          onClick={() => setTab('all-forms')}
          icon={<ListChecks className="h-3.5 w-3.5" />}
          label="All Forms"
        />
      </div>

      {tab === 'by-visit' ? (
        <ByVisitTab studyOptions={studyOptions} canConfigure={canConfigure} canAttach={canAttach} />
      ) : (
        <AllFormsTab canConfigure={canConfigure} studyLabelById={studyLabelById} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab chrome
// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${active
        ? 'border-accent-600 text-accent-700'
        : 'border-transparent text-ink-muted hover:text-ink-primary'
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// By-Visit tab — visit list on left, form checkboxes on right
// ---------------------------------------------------------------------------

function ByVisitTab({
  studyOptions,
  canConfigure,
  canAttach,
}: {
  studyOptions: SingleSelectOption[];
  canConfigure: boolean;
  canAttach: boolean;
}) {
  const [searchParams] = useSearchParams();
  // Study Workspace Hub's "Edit forms" quick action links here with
  // ?study_id=... so the picker lands pre-scoped instead of unfiltered.
  // The study filter only makes sense here — a visit schedule only exists
  // per-study — so it lives on this tab, not shared with All Forms.
  const [studyId, setStudyId] = useState(() => searchParams.get('study_id') ?? '');
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewVisitId, setPreviewVisitId] = useState<string | null>(null);

  const scheduleQ = useQuery({
    queryKey: ['study', studyId, 'schedule'],
    queryFn: () => schedule.get(studyId),
    enabled: !!studyId,
  });

  const visits = useMemo(() => {
    const raw = scheduleQ.data?.visits ?? [];
    return [...raw].sort((a, b) => (a.visit_order ?? 0) - (b.visit_order ?? 0));
  }, [scheduleQ.data]);

  // Default: select the first visit whenever the study or list changes.
  const firstVisitId = visits[0]?.template_visit_id ?? null;
  const effectiveVisitId = selectedVisitId ?? firstVisitId;

  const studyPicker = (
    <div className="card p-3">
      <label className="label text-xs">Study *</label>
      <div className="max-w-xl">
        <SingleSelectSearch
          options={studyOptions}
          value={studyId}
          onChange={(v) => { setStudyId(v ?? ''); setSelectedVisitId(null); }}
          placeholder="Pick a study to see its visits"
        />
      </div>
    </div>
  );

  if (!studyId) {
    return (
      <div className="space-y-3">
        {studyPicker}
        <div className="card p-10 text-center text-sm text-ink-muted">
          Pick a study above to continue.
        </div>
      </div>
    );
  }
  if (scheduleQ.isLoading) {
    return (
      <div className="space-y-3">
        {studyPicker}
        <div className="card flex items-center justify-center gap-2 p-10 text-xs text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading visit schedule…
        </div>
      </div>
    );
  }
  if (visits.length === 0) {
    return (
      <div className="space-y-3">
        {studyPicker}
        <div className="card p-10 text-center text-sm text-ink-muted">
          This study has no visit templates yet. Add visits in the{' '}
          <Link to={`/studies/${encodeURIComponent(studyId)}/schedule`} className="underline">
            Schedule of Activities
          </Link>{' '}
          first — then come back to assign forms.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {studyPicker}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[280px_1fr]">
        {/* Visit list */}
        <aside className="card overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-2 text-[10px] uppercase tracking-wider text-ink-muted">
            Visits ({visits.length})
          </div>
          <ul className="divide-y divide-surface-border">
            {visits.map((v) => (
              <VisitRow
                key={v.template_visit_id}
                visit={v}
                active={v.template_visit_id === effectiveVisitId}
                onClick={() => setSelectedVisitId(v.template_visit_id)}
                onPreview={() => setPreviewVisitId(v.template_visit_id)}
              />
            ))}
          </ul>
        </aside>

        {/* Right: forms for the selected visit */}
        {effectiveVisitId ? (
          <VisitFormsPanel
            visitTemplateId={effectiveVisitId}
            studyId={studyId}
            canConfigure={canConfigure}
            canAttach={canAttach}
            onNewForm={() => setCreating(true)}
          />
        ) : (
          <div className="card p-10 text-center text-sm text-ink-muted">
            Pick a visit on the left to see its assigned forms.
          </div>
        )}

        {creating && effectiveVisitId && (
          <CreateFormModal
            studyId={studyId}
            visitTemplateId={effectiveVisitId}
            studyOptions={studyOptions}
            onClose={() => setCreating(false)}
          />
        )}

        {previewVisitId && (
          <PreviewVisitFormsModal
            visitTemplateId={previewVisitId}
            onClose={() => setPreviewVisitId(null)}
          />
        )}
      </div>
    </div>
  );
}

function VisitRow({
  visit,
  active,
  onClick,
  onPreview,
}: {
  visit: VisitTemplateRow;
  active: boolean;
  onClick: () => void;
  onPreview: () => void;
}) {
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center justify-between gap-2 px-3 py-2 pr-8 text-left text-xs transition-colors ${active ? 'bg-accent-50/60 text-ink-primary' : 'hover:bg-surface-muted/40 text-ink-secondary'
          }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-surface-muted/60 px-1 text-[10px] font-semibold">
              #{visit.visit_order ?? '?'}
            </span>
            <span className="truncate font-medium">{visit.visit_name}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-ink-muted">
            <span>Day {visit.nominal_day ?? 0}</span>
            {(visit.window_minus_days ?? 0) + (visit.window_plus_days ?? 0) > 0 && (
              <span>
                -{visit.window_minus_days ?? 0}/+{visit.window_plus_days ?? 0}d
              </span>
            )}
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-muted opacity-60 transition hover:bg-surface-muted hover:text-accent-700 hover:opacity-100 group-hover:opacity-100"
        title="Preview all assigned forms for this visit as one combined page"
        aria-label={`Preview forms for ${visit.visit_name}`}
      >
        <Eye className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function VisitFormsPanel({
  visitTemplateId,
  studyId,
  canConfigure,
  canAttach,
  onNewForm,
}: {
  visitTemplateId: string;
  studyId: string;
  canConfigure: boolean;
  canAttach: boolean;
  onNewForm: () => void;
}) {
  const qc = useQueryClient();
  const formsQ = useQuery({
    queryKey: ['visit-template', visitTemplateId, 'forms'],
    queryFn: () => formVisitBindings.listFormsForVisit(visitTemplateId),
  });

  const toggle = useMutation({
    mutationFn: async ({ formId, assign }: { formId: string; assign: boolean }) => {
      if (assign) {
        await formVisitBindings.assign(formId, visitTemplateId);
      } else {
        await formVisitBindings.unassign(formId, visitTemplateId);
      }
    },
    onSuccess: () => {
      // Forms list and preview both change with the visit's bindings.
      qc.invalidateQueries({ queryKey: ['visit-template', visitTemplateId] });
      // Also invalidate the flat forms list — its binding-count column
      // (if surfaced later) would go stale.
      qc.invalidateQueries({ queryKey: ['forms', 'list'] });
    },
    onError: (e) => toast.error(errorText(e, 'Toggle failed')),
  });

  const rows = useMemo(() => formsQ.data?.forms ?? [], [formsQ.data]);
  const locked = !!formsQ.data?.study_locked;
  // The server lists assigned forms first, in the visit's sequence.
  const serverAssigned = useMemo(() => rows.filter((f) => f.assigned), [rows]);
  const unassigned = rows.filter((f) => !f.assigned);
  const assignedCount = serverAssigned.length;

  // Local sequence so a drag lands immediately; reset whenever the server's changes.
  const serverOrderKey = serverAssigned.map((f) => f.form_id).join(',');
  const [order, setOrder] = useState<string[]>(() => serverAssigned.map((f) => f.form_id));
  useEffect(() => {
    setOrder(serverAssigned.map((f) => f.form_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverOrderKey]);
  const byId = new Map(serverAssigned.map((f) => [f.form_id, f]));
  const assigned = order.map((id) => byId.get(id)).filter((f): f is FormForVisitRow => !!f);

  // Whoever can attach a form can place it in the sequence (Harish, 13 Oct 2026).
  const canReorder = canAttach && !locked && assigned.length > 1;

  const reorder = useMutation({
    mutationFn: (formIds: string[]) => formVisitBindings.reorder(visitTemplateId, formIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visit-template', visitTemplateId] }),
    onError: (e) => {
      setOrder(serverAssigned.map((f) => f.form_id));
      qc.invalidateQueries({ queryKey: ['visit-template', visitTemplateId, 'forms'] });
      toast.error(errorText(e, 'Could not save the form order'));
    },
  });

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= order.length) return;
    const next = arrayMove(order, from, to);
    setOrder(next);
    reorder.mutate(next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    move(order.indexOf(String(active.id)), order.indexOf(String(over.id)));
  };

  const rowProps = (f: FormForVisitRow) => ({
    form: f,
    canConfigure,
    canAttach,
    locked,
    disabled: toggle.isPending,
    onToggle: (assign: boolean) => toggle.mutate({ formId: f.form_id, assign }),
    visitTemplateId,
    studyIdForLink: studyId,
  });

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted/40 px-3 py-2">
        <div className="text-[11px] text-ink-secondary">
          <span className="font-semibold text-ink-primary">{formsQ.data?.visit_name ?? '—'}</span>
          <span className="ml-2 text-ink-muted">
            {assignedCount} of {rows.length} form{rows.length === 1 ? '' : 's'} assigned
          </span>
          {reorder.isPending && (
            <span className="ml-2 inline-flex items-center gap-1 text-ink-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving order…
            </span>
          )}
        </div>
        {canConfigure && !locked && (
          <button
            type="button"
            onClick={onNewForm}
            className="rounded bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-700"
          >
            <Plus className="mr-1 inline h-3 w-3" /> New form
          </button>
        )}
      </div>

      {formsQ.isLoading ? (
        <div className="flex items-center justify-center gap-2 p-8 text-xs text-ink-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading forms…
        </div>
      ) : formsQ.isError ? (
        <div className="m-3 rounded border border-danger-300 bg-danger-50 p-3 text-xs text-danger-800">
          <strong>Could not load forms for this visit.</strong>
          <div className="mt-1 text-[11px]">
            {formsQ.error instanceof ApiError
              ? formsQ.error.message
              : (formsQ.error as Error)?.message}
          </div>
          <div className="mt-2 text-[11px] text-ink-muted">
            If this endpoint is new to your build, the backend needs a restart
            (<code className="font-mono">serverless-offline</code> only picks up new routes on boot).
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-xs text-ink-muted">
          This study has no forms yet.
          {canConfigure && (
            <>
              {' '}Click <strong>New form</strong> above to create one and assign it to this visit.
            </>
          )}
        </div>
      ) : (
        <>
          {locked && (
            <div className="flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              <Lock className="h-3 w-3" /> This study is locked. Its visits' forms, conditions and order can't be changed.
            </div>
          )}
          {assigned.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={order} strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-surface-border">
                  {assigned.map((f, i) => (
                    <SortableFormAssignmentRow
                      key={f.form_id}
                      {...rowProps(f)}
                      position={i + 1}
                      canReorder={canReorder}
                      reorderPending={reorder.isPending}
                      onMoveUp={i > 0 ? () => move(i, i - 1) : undefined}
                      onMoveDown={i < assigned.length - 1 ? () => move(i, i + 1) : undefined}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
          {unassigned.length > 0 && (
            <>
              {assigned.length > 0 && (
                <div className="border-y border-surface-border bg-surface-muted/30 px-3 py-1 text-[10px] uppercase tracking-wider text-ink-muted">
                  Not on this visit
                </div>
              )}
              <ul className="divide-y divide-surface-border">
                {unassigned.map((f) => (
                  <FormAssignmentRow key={f.form_id} {...rowProps(f)} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * An assigned form's row with its place in the visit's sequence: number,
 * drag handle and up/down buttons (the buttons are the keyboard-friendly path).
 */
function SortableFormAssignmentRow({
  position,
  canReorder,
  reorderPending,
  onMoveUp,
  onMoveDown,
  ...rowProps
}: FormAssignmentRowProps & {
  position: number;
  canReorder: boolean;
  reorderPending: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowProps.form.form_id,
    disabled: !canReorder || reorderPending,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } : {}),
  };
  const arrowClass = 'rounded p-0.5 text-ink-muted hover:bg-surface-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-30';

  const leading = (
    <div className="flex shrink-0 items-center gap-1">
      {canReorder && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab rounded p-0.5 text-ink-muted hover:bg-surface-muted active:cursor-grabbing"
          title="Drag to change the order of forms in this visit"
          aria-label={`Move ${rowProps.form.name}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <span
        className="w-5 text-center font-mono text-[10px] font-semibold text-ink-muted"
        title="Order in which this form appears at the visit"
      >
        {position}
      </span>
    </div>
  );

  const trailing = canReorder ? (
    <div className="flex shrink-0 flex-col">
      <button
        type="button"
        onClick={onMoveUp}
        disabled={!onMoveUp || reorderPending}
        className={arrowClass}
        title="Move up"
        aria-label={`Move ${rowProps.form.name} up`}
      >
        <ChevronUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={!onMoveDown || reorderPending}
        className={arrowClass}
        title="Move down"
        aria-label={`Move ${rowProps.form.name} down`}
      >
        <ChevronDown className="h-3 w-3" />
      </button>
    </div>
  ) : null;

  return (
    <FormAssignmentRow
      {...rowProps}
      liRef={setNodeRef}
      liStyle={style}
      leading={leading}
      trailing={trailing}
    />
  );
}

// ---------------------------------------------------------------------------
// Preview visit forms — renders every assigned + published form as one
// scrolling page so the designer sees what the coordinator will actually
// fill out at this visit. Read-only, non-interactive.
// ---------------------------------------------------------------------------

function PreviewVisitFormsModal({
  visitTemplateId,
  onClose,
}: {
  visitTemplateId: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['visit-template', visitTemplateId, 'preview'],
    queryFn: () => formVisitBindings.previewForVisit(visitTemplateId),
  });

  const totalFields = (q.data?.forms ?? []).reduce((sum, f) => sum + (f.fields?.length ?? 0), 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">
              Preview — {q.data?.visit_name ?? '…'}
            </h3>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {q.isLoading
                ? 'Loading…'
                : q.data
                  ? `${q.data.forms.length} form${q.data.forms.length === 1 ? '' : 's'} · ${totalFields} field${totalFields === 1 ? '' : 's'} · read-only preview of what the coordinator will fill out at this visit`
                  : 'No data'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto bg-surface-muted/20 p-4">
          {q.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-xs text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          ) : q.isError ? (
            <div className="rounded border border-danger-300 bg-danger-50 p-3 text-xs text-danger-800">
              <strong>Couldn't load preview.</strong>
              <div className="mt-1">
                {q.error instanceof ApiError ? q.error.message : (q.error as Error)?.message}
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">
                If the endpoint is new to your build, restart the backend — serverless-offline only registers new routes at boot.
              </div>
            </div>
          ) : (q.data?.forms ?? []).length === 0 ? (
            <div className="rounded border border-dashed border-surface-border bg-surface-card p-8 text-center text-xs text-ink-muted">
              No <strong>published</strong> forms assigned to this visit. Preview only
              includes forms in <span className="rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium text-emerald-700">Published</span> state — draft forms are hidden until published.
            </div>
          ) : (
            <div className="space-y-3">
              {(q.data?.forms ?? []).map((f) => (
                <PreviewFormSection key={f.form_id} form={f} />
              ))}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end border-t border-surface-border px-4 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function PreviewFormSection({
  form,
}: {
  form: {
    form_id: string;
    reference_number: string;
    form_key: string;
    name: string;
    description: string;
    version_label: string;
    fields: Array<{
      field_key: string;
      field_label: string;
      field_type: string;
      config?: {
        required?: boolean;
        help_text?: string;
        options?: Array<{ value: string; label: string }>;
      };
    }>;
  };
}) {
  return (
    <section className="rounded border border-surface-border bg-surface-card p-3">
      <div className="mb-2 flex items-center justify-between border-b border-surface-border pb-2">
        <div>
          <h4 className="text-sm font-semibold text-ink-primary">{form.name}</h4>
          {form.description && (
            <p className="mt-0.5 text-[11px] text-ink-muted">{form.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-[10px] text-ink-muted">
          <span className="font-mono">{form.reference_number}</span>
          <span>·</span>
          <span>{form.version_label}</span>
          <span>·</span>
          <span>{form.fields.length} field{form.fields.length === 1 ? '' : 's'}</span>
        </div>
      </div>
      {form.fields.length === 0 ? (
        <div className="rounded border border-dashed border-surface-border p-4 text-center text-[11px] text-ink-muted">
          This form has no fields.
        </div>
      ) : (
        <div className="space-y-3">
          {form.fields.map((field) => (
            <PreviewFieldRender key={field.field_key} field={field} />
          ))}
        </div>
      )}
    </section>
  );
}

function PreviewFieldRender({
  field,
}: {
  field: {
    field_key: string;
    field_label: string;
    field_type: string;
    config?: {
      required?: boolean;
      help_text?: string;
      options?: Array<{ value: string; label: string }>;
    };
  };
}) {
  const cfg = field.config ?? {};
  const label = (
    <span className="text-xs font-medium text-ink-secondary">
      {field.field_label}
      {cfg.required && <span className="ml-1 text-danger-600">*</span>}
    </span>
  );
  const help = cfg.help_text ? (
    <p className="mt-0.5 text-[10px] text-ink-muted">{cfg.help_text}</p>
  ) : null;
  const disabledInput = 'input mt-1 cursor-not-allowed bg-surface-muted/40 text-xs opacity-70';

  switch (field.field_type) {
    case 'text':
    case 'number':
    case 'date':
    case 'datetime':
    case 'calculated':
      return (
        <label className="block">
          {label}
          <input
            type={
              field.field_type === 'number'
                ? 'number'
                : field.field_type === 'date'
                  ? 'date'
                  : field.field_type === 'datetime'
                    ? 'datetime-local'
                    : 'text'
            }
            disabled
            placeholder={
              field.field_type === 'calculated'
                ? '(derived)'
                : field.field_type === 'text'
                  ? 'text'
                  : ''
            }
            className={disabledInput}
          />
          {help}
        </label>
      );
    case 'textarea':
      return (
        <label className="block">
          {label}
          <textarea rows={2} disabled className={disabledInput} />
          {help}
        </label>
      );
    case 'select':
      return (
        <label className="block">
          {label}
          <select disabled className={disabledInput}>
            <option>— select —</option>
            {(cfg.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {help}
        </label>
      );
    case 'radio':
      return (
        <div>
          {label}
          <div className="mt-1 space-y-0.5">
            {(cfg.options ?? []).map((o) => (
              <label key={o.value} className="flex items-center gap-1.5 text-xs">
                <input type="radio" disabled />
                <span className="text-ink-primary">{o.label}</span>
              </label>
            ))}
          </div>
          {help}
        </div>
      );
    case 'checkbox':
      return (
        <div>
          {label}
          <div className="mt-1 space-y-0.5">
            {(cfg.options ?? []).length > 0 ? (
              (cfg.options ?? []).map((o) => (
                <label key={o.value} className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" disabled />
                  <span className="text-ink-primary">{o.label}</span>
                </label>
              ))
            ) : (
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" disabled />
                <span className="text-ink-primary">{field.field_label}</span>
              </label>
            )}
          </div>
          {help}
        </div>
      );
    default:
      return (
        <label className="block">
          {label}
          <input type="text" disabled className={disabledInput} placeholder={`(${field.field_type})`} />
          {help}
        </label>
      );
  }
}

interface FormAssignmentRowProps {
  form: FormForVisitRow;
  canConfigure: boolean;
  canAttach: boolean;
  /** Study locked: visit set-up is read-only. */
  locked: boolean;
  disabled: boolean;
  onToggle: (assign: boolean) => void;
  visitTemplateId: string;
  studyIdForLink: string;
}

function FormAssignmentRow({
  form,
  canConfigure,
  canAttach,
  locked,
  disabled,
  onToggle,
  visitTemplateId,
  studyIdForLink: _studyIdForLink,
  liRef,
  liStyle,
  leading,
  trailing,
}: FormAssignmentRowProps & {
  liRef?: (el: HTMLLIElement | null) => void;
  liStyle?: CSSProperties;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hasRule = !!form.visibility_rule_json;
  // Designers attach any form except archived ones; the study team only
  // published ones (and may always take a form off a visit) — Harish, 2 Oct
  // 2026. Nothing changes on a locked study (Harish, 13 Oct 2026).
  const canToggle = !locked && (
    form.assigned
      ? canConfigure || canAttach
      : form.status !== 'archived' && (canConfigure || (canAttach && form.status === 'published'))
  );

  return (
    <li ref={liRef} style={liStyle} className="flex items-center gap-2 bg-surface-card px-3 py-2 text-xs">
      {leading}
      <button
        type="button"
        onClick={() => onToggle(!form.assigned)}
        disabled={!canToggle || disabled}
        className={`shrink-0 rounded p-0.5 transition-colors ${canToggle && !disabled
          ? 'hover:bg-surface-muted/50'
          : 'cursor-not-allowed opacity-50'
          }`}
        title={
          !canToggle
            ? locked
              ? 'This study is locked'
              : form.status === 'archived'
                ? 'Archived forms can\'t be attached'
                : canAttach
                  ? 'Only published forms can be attached. Ask the study designer to publish it first.'
                  : 'You don\'t have permission to change bindings'
            : form.assigned
              ? 'Unassign this form from the visit'
              : 'Assign this form to the visit'
        }
      >
        {form.assigned ? (
          <CheckSquare className="h-4 w-4 text-accent-600" />
        ) : (
          <Square className="h-4 w-4 text-ink-muted" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {canConfigure ? (
            <Link
              to={`/forms/${encodeURIComponent(form.reference_number)}`}
              className="font-medium text-accent-700 hover:underline"
            >
              {form.name}
            </Link>
          ) : (
            <span className="font-medium text-ink-primary">{form.name}</span>
          )}
          <span className="font-mono text-[10px] text-ink-muted">{form.reference_number}</span>
          <span
            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[form.status]}`}
          >
            {STATUS_LABELS[form.status]}
          </span>
          {hasRule && (
            <span
              className="inline-flex items-center gap-0.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
              title="This form is only shown when its visibility conditions match."
            >
              <Zap className="h-2.5 w-2.5" /> Conditional
            </span>
          )}
        </div>
        {form.description && (
          <div className="mt-0.5 truncate text-[11px] text-ink-muted">{form.description}</div>
        )}
      </div>
      {form.assigned && canConfigure && !locked && (
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className={`shrink-0 rounded border px-2 py-1 text-[11px] font-medium ${
            hasRule
              ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
              : 'border-surface-border bg-surface-card text-ink-secondary hover:bg-surface-muted/50'
          }`}
        >
          {hasRule ? 'Edit conditions' : 'Set conditions'}
        </button>
      )}
      {trailing}

      {drawerOpen && (
        <VisibilityRuleDrawer
          form={form}
          visitTemplateId={visitTemplateId}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </li>
  );
}

function VisibilityRuleDrawer({
  form,
  visitTemplateId,
  onClose,
}: {
  form: FormForVisitRow;
  visitTemplateId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const initial = useMemo<ConditionExpr | null>(() => {
    if (!form.visibility_rule_json) return null;
    try {
      const parsed = JSON.parse(form.visibility_rule_json);
      return parsed && typeof parsed === 'object' ? (parsed as ConditionExpr) : null;
    } catch {
      return null;
    }
  }, [form.visibility_rule_json]);
  const [rule, setRule] = useState<ConditionExpr | null>(initial);

  const save = useMutation({
    mutationFn: () =>
      formVisitBindings.assign(form.form_id, visitTemplateId, { visibility_rule: rule }),
    onSuccess: () => {
      toast.success(rule ? 'Conditions saved.' : 'Conditions cleared.');
      qc.invalidateQueries({ queryKey: ['visit-template', visitTemplateId, 'forms'] });
      onClose();
    },
    onError: (e) => toast.error(errorText(e, 'Save failed')),
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <aside className="relative flex h-full w-full max-w-lg flex-col overflow-hidden bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">Visibility conditions</h3>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {form.name} · shown only when conditions match.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <RuleBuilder value={rule} onChange={setRule} />
        </div>

        <footer className="flex items-center justify-between border-t border-surface-border px-4 py-2.5">
          <button
            type="button"
            onClick={() => setRule(null)}
            disabled={!rule || save.isPending}
            className="rounded px-3 py-1.5 text-xs font-medium text-danger-700 hover:bg-danger-50 disabled:opacity-50"
          >
            Clear conditions
          </button>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onClose}
              disabled={save.isPending}
              className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save conditions'}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// All-Forms tab — legacy flat list
// ---------------------------------------------------------------------------

function AllFormsTab({
  canConfigure,
  studyLabelById,
}: {
  canConfigure: boolean;
  studyLabelById: Map<string, string>;
}) {
  const [statusFilter, setStatusFilter] = useState<'' | FormStatus>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Bug found live (UAT: "All forms should be showing all forms for the
  // site irrespective of study — study filter for by visit only"). Always
  // site-wide here — no study_id at all. The backend scopes this to every
  // study visible at the caller's active site (handlers/forms/list.ts).
  const formsQ = useQuery({
    queryKey: ['forms', 'list', 'all', statusFilter],
    queryFn: () =>
      forms.list({
        status: statusFilter || undefined,
        limit: 500,
      }),
  });

  const rows = formsQ.data?.rows ?? [];
  const blinded = formsQ.data?.blinded === true;
  const blindedRoleRef = formsQ.data?.blinded_role_master_ref ?? null;

  // Reset to page 1 whenever the underlying result set changes shape.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (blinded) {
    return <BlindedNotice contentLabel="Forms" roleMasterRef={blindedRoleRef} />;
  }

  return (
    <>
      <div className="card p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label text-xs">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="mt-1 rounded border border-surface-border px-3 py-1.5 text-xs"
            >
              <option value="">Any</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="ml-auto text-xs text-ink-muted">
            {!formsQ.isLoading && `${rows.length} form${rows.length === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {formsQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-xs text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading forms…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-muted">
            No forms match the current filters.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-surface-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Ref</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Study</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {pagedRows.map((row) => (
                <FormListRow
                  key={row.form_id}
                  row={row}
                  canConfigure={canConfigure}
                  studyLabel={studyLabelById.get(row.study_id) ?? `Study ${row.study_id.slice(0, 8)}`}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-ink-muted">
          <span>
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} of {rows.length}
          </span>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded border border-surface-border px-2 py-1 text-xs"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="rounded border border-surface-border px-2 py-1 font-medium hover:bg-surface-muted/50 disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded border border-surface-border px-2 py-1 font-medium hover:bg-surface-muted/50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function FormListRow({
  row,
  canConfigure,
  studyLabel,
}: {
  row: FormRow;
  canConfigure: boolean;
  studyLabel?: string;
}) {
  return (
    <tr className="hover:bg-surface-muted/30">
      <td className="px-3 py-2 text-[12px] text-ink-secondary">{row.reference_number}</td>
      <td className="px-3 py-2">
        {canConfigure ? (
          <Link
            to={`/forms/${encodeURIComponent(row.reference_number)}`}
            className="text-accent-700 hover:underline"
          >
            {row.name}
          </Link>
        ) : (
          row.name
        )}
      </td>
      {studyLabel !== undefined && (
        <td className="px-3 py-2 text-ink-secondary">{studyLabel}</td>
      )}
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      </td>
      <td className="px-3 py-2 text-ink-muted">{fmtDateTime(row.updated_at)}</td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Create-form modal — auto-assigns to a visit if visitTemplateId is passed
// ---------------------------------------------------------------------------

function CreateFormModal({
  studyId,
  visitTemplateId,
  studyOptions,
  onClose,
}: {
  studyId: string;
  visitTemplateId?: string;
  /** Bug found live (UAT: "ESource Forms: This should allow copy from
   *  other studies as a template. We cannot copy a form from one study
   *  to another."). Used to label + group "start from" candidates that
   *  come from other studies at this site. */
  studyOptions: SingleSelectOption[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startFromFormId, setStartFromFormId] = useState('');

  const studyLabelById = useMemo(
    () => new Map(studyOptions.map((o) => [o.value, o.label])),
    [studyOptions],
  );

  // Site-wide, not study_id-scoped — every study's forms are fair game
  // as a starting template, not just the one being worked in.
  const existingFormsQ = useQuery({
    queryKey: ['forms', 'startfrom-picker', 'all'],
    queryFn: () => forms.list({ limit: 500 }),
    staleTime: 60_000,
  });

  const existingForms = useMemo(
    () =>
      (existingFormsQ.data?.rows ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [existingFormsQ.data],
  );

  // Group by study so the dropdown reads as "which study is this template
  // from", not a flat list where same-named forms are indistinguishable.
  const formsByStudy = useMemo(() => {
    const groups = new Map<string, FormRow[]>();
    for (const f of existingForms) {
      const list = groups.get(f.study_id) ?? [];
      list.push(f);
      groups.set(f.study_id, list);
    }
    return Array.from(groups.entries())
      .map(([sid, list]) => ({ studyId: sid, label: studyLabelById.get(sid) ?? `Study ${sid.slice(0, 8)}`, forms: list }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [existingForms, studyLabelById]);

  // Global read-only CDISC form-template library — clone-to-edit, like
  // the binder templates. Offered as an optgroup in the Start-from picker.
  const libraryQ = useQuery({
    queryKey: ['forms', 'library'],
    queryFn: () => forms.library(),
    staleTime: 60_000,
  });
  const libraryTemplates = useMemo(
    () => (libraryQ.data?.rows ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [libraryQ.data],
  );

  const create = useMutation({
    mutationFn: async () => {
      let created:
        | { form_id: string; reference_number: string }
        | { form_id: string; reference_number: string; source_form_id: string };

      if (startFromFormId.startsWith('tpl:')) {
        created = await forms.cloneTemplate(startFromFormId.slice(4), {
          target_study_id: studyId,
          name: name.trim(),
        });
      } else if (startFromFormId) {
        created = await forms.clone(startFromFormId, {
          name: name.trim(),
          target_study_id: studyId,
        });
      } else {
        created = await forms.create({
          study_id: studyId,
          name: name.trim(),
          description: description.trim() || undefined,
        });
      }

      if (visitTemplateId) {
        try {
          await formVisitBindings.assign(created.form_id, visitTemplateId);
        } catch (err) {
          toast.error(
            `Form created but auto-assign to visit failed: ${err instanceof ApiError ? err.message : (err as Error).message
            }`,
          );
        }
      }
      return created;
    },
    onSuccess: (r) => {
      toast.success(
        visitTemplateId
          ? `Form ${r.reference_number} created and assigned to this visit.`
          : `Form ${r.reference_number} created.`,
      );
      qc.invalidateQueries({ queryKey: ['forms', 'list'] });
      if (visitTemplateId) {
        qc.invalidateQueries({ queryKey: ['visit-template', visitTemplateId, 'forms'] });
      }
      onClose();
      navigate(`/forms/${encodeURIComponent(r.reference_number)}`);
    },
    onError: (e) => toast.error(errorText(e, 'Create failed')),
  });

  const canCreate = !!name.trim() && !create.isPending;
  const startingFrom = existingForms.find((f) => f.form_id === startFromFormId);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink-primary">
            New eSource form{visitTemplateId ? ' — auto-assigns to visit' : ''}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="space-y-3 px-4 py-4">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Start from</span>
            <select
              value={startFromFormId}
              onChange={(e) => {
                const val = e.target.value;
                setStartFromFormId(val);
                if (val && !name.trim()) {
                  if (val.startsWith('tpl:')) {
                    const tpl = libraryTemplates.find((t) => `tpl:${t.form_template_id}` === val);
                    if (tpl) setName(tpl.name);
                  } else {
                    const src = existingForms.find((f) => f.form_id === val);
                    if (src) setName(`${src.name} (Copy)`);
                  }
                }
              }}
              disabled={existingFormsQ.isLoading}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm"
            >
              <option value="">Blank form (start from scratch)</option>
              {libraryTemplates.length > 0 && (
                <optgroup label="Standard templates (CDISC library)">
                  {libraryTemplates.map((t) => (
                    <option key={t.form_template_id} value={`tpl:${t.form_template_id}`}>
                      {t.name}{t.domain_code ? ` · ${t.domain_code}` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {formsByStudy.map((group) => (
                <optgroup key={group.studyId} label={group.label}>
                  {group.forms.map((f) => (
                    <option key={f.form_id} value={f.form_id}>
                      {f.name} · {f.reference_number}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {startingFrom && (
              <p className="mt-1 text-[11px] text-ink-muted">
                Fields from <strong>{startingFrom.name}</strong> will be copied into a new
                draft. The original form is untouched.
              </p>
            )}
          </label>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Form name <span className="text-danger-600">*</span></span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vitals, Adverse Event Follow-up"
              className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm"
            />
          </label>

          {!startFromFormId && (
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm"
              />
            </label>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!canCreate}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {create.isPending ? (
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
            ) : (
              <Check className="mr-1 inline h-3 w-3" />
            )}
            {startFromFormId ? 'Create from copy & open designer' : 'Create & open designer'}
          </button>
        </footer>
      </div>
    </div>
  );
}
